import { Link, PodcastThemeInfo } from '../types';
import { apiClient, ApiError } from './apiClient';

const convertLinkResponse = (link: any): Link => ({
    id: link.id,
    userId: link.userID,
    path: link.path,
    type: link.type || 'podcast',
    name: link.name || 'Untitled',
    coverImage: link.coverImage || '',
    platforms: link.platforms || [],
    itemId: link.itemID,
    createdAt: link.createdAt,
    updatedAt: link.updatedAt,
});

export const podcastsApi = {
    async getThemes(): Promise<PodcastThemeInfo[]> {
        try {
            const response = await apiClient.request<PodcastThemeInfo[]>('/api/podcasts/themes', {
                method: 'GET',
            });

            if (!response.success || !response.data) {
                throw new ApiError(404, 'Failed to fetch themes');
            }

            return response.data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(404, 'Failed to fetch themes');
        }
    },

    async getPodcast(podcastId: string): Promise<Link> {
        try {
            const response = await apiClient.request<any>(`/api/podcasts/${podcastId}`, {
                method: 'GET',
            });

            if (!response.success || !response.data) {
                throw new ApiError(404, 'Podcast not found');
            }

            const podcast = response.data;
            return {
                id: podcast.id,
                userId: podcast.userID,
                path: `podcast-${podcast.id}`,
                type: 'podcast' as const,
                name: podcast.name,
                title: podcast.title,
                description: podcast.description,
                coverImage: podcast.coverImage,
                platforms: podcast.platforms,
                itemId: podcast.id,
                theme: podcast.theme,
                createdAt: podcast.createdAt,
                updatedAt: podcast.updatedAt,
            };
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(404, 'Podcast not found');
        }
    },

    async createPodcast(podcastData: {
        name: string;
        title?: string;
        description?: string;
        coverImage: string;
        platforms: any;
        path: string;
        theme?: string;
    }): Promise<Link> {
        try {
            const response = await apiClient.request<any>('/api/podcasts', {
                method: 'POST',
                body: JSON.stringify(podcastData),
            });

            if (!response.success || !response.data) {
                throw new ApiError(400, 'Failed to create podcast');
            }

            return convertLinkResponse(response.data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to create podcast');
        }
    },

    async updatePodcast(podcastData: {
        id: string;
        name: string;
        title?: string;
        description?: string;
        coverImage: string;
        platforms: any;
        theme?: string;
    }): Promise<Link> {
        try {
            const response = await apiClient.request<any>(
                `/api/podcasts/${podcastData.id}`,
                {
                    method: 'PUT',
                    body: JSON.stringify(podcastData),
                }
            );

            if (!response.success || !response.data) {
                throw new ApiError(400, 'Failed to update podcast');
            }

            return convertLinkResponse(response.data);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to update podcast');
        }
    },

    async deletePodcast(podcastId: string): Promise<void> {
        try {
            await apiClient.request<{ message: string }>(
                `/api/podcasts/${podcastId}`,
                {
                    method: 'DELETE',
                }
            );
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to delete podcast');
        }
    },

    async previewPodcast(previewData: {
        name: string;
        title?: string;
        description?: string;
        coverImage: string;
        platforms: any;
        theme?: string;
    }): Promise<string> {
        try {
            const response = await fetch(`${apiClient['baseUrl']}/api/podcasts/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(previewData),
            });

            if (!response.ok) {
                throw new ApiError(response.status, 'Failed to generate preview');
            }

            return await response.text();
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(500, 'Failed to generate preview');
        }
    },
};
